import React, { Component, type ComponentType } from 'react';
import { styled, css } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { HEADER_HEIGHT } from '../../../../../model/table';
import type {
	ColumnDuration,
	TimelineDuration,
	TimelineWindow,
	TimelineMode,
} from '../../../../../model/timeline';
import { HIDE_SCROLLBAR_OFFSET_PX } from '../../../common/constants';
import { withSynchronizedScroll } from '../../../common/context/scroll-synchronizer';
import type { APIProps as HeaderCellsProps } from '../header-cells';

type APIProps = {
	isHighlightToday: boolean;
	columnDurations: ColumnDuration[];
	timelineMode: TimelineMode;
	timelineDuration: TimelineDuration;
	timelineWindow: TimelineWindow;
	today: Date;
	HeaderCells: ComponentType<HeaderCellsProps>;
	timelineDefaultPosition: number;
	width: number;
	onTimelineUpdated: () => void;
};

type Props = APIProps;

type State = {
	isScrollSyncEnabled: boolean;
	timelineDefaultPosition: number;
};

// eslint-disable-next-line jira/react/no-class-components
class ChartHeader extends Component<Props, State> {
	static getDerivedStateFromProps(nextProps: Props, prevState: State) {
		if (
			nextProps.timelineDefaultPosition !== prevState.timelineDefaultPosition &&
			prevState.isScrollSyncEnabled === true
		) {
			return {
				isScrollSyncEnabled: false,
			};
		}

		return null;
	}

	constructor(props: Props) {
		super(props);

		/**
		 * We set timelineDefaultPosition in state even though we are not using it
		 * so that we can compare it in getDerivedStateFromProps
		 */
		this.state = {
			isScrollSyncEnabled: true,
			timelineDefaultPosition: props.timelineDefaultPosition,
		};
	}

	componentDidMount() {
		this.listenToHeaderScroll();
	}

	componentDidUpdate(prevProps: Props, prevState: State) {
		if (this.props.timelineDefaultPosition !== prevProps.timelineDefaultPosition) {
			this.setState({
				timelineDefaultPosition: this.props.timelineDefaultPosition,
				isScrollSyncEnabled: true,
			});
		}

		if (
			this.state.isScrollSyncEnabled &&
			this.state.isScrollSyncEnabled !== prevState.isScrollSyncEnabled
		) {
			// call onTimelineUpdated once header has done scrolling
			this.listenToHeaderScroll();
		}
	}

	onScroll = () => {
		this.props.onTimelineUpdated();
		this.headerScrollRef && this.headerScrollRef.removeEventListener('scroll', this.onScroll);
	};

	onSetScrollbarRef = (elem: HTMLElement | null) => {
		this.headerScrollRef = elem;
	};

	listenToHeaderScroll() {
		this.headerScrollRef && this.headerScrollRef.addEventListener('scroll', this.onScroll);
	}

	// @ts-expect-error - TS2564 - Property 'headerScrollRef' has no initializer and is not definitely assigned in the constructor.
	headerScrollRef: HTMLElement | null;

	render() {
		const {
			today,
			timelineMode,
			timelineDuration,
			columnDurations,
			isHighlightToday,
			HeaderCells,
			width,
			timelineWindow,
		} = this.props;
		const { isScrollSyncEnabled } = this.state;

		const ScrollbarComponent = isScrollSyncEnabled ? SynchronizedScrollbar : HiddenScrollbar;

		return (
			<Heading size="xsmall" as="div">
				<Header>
					<ScrollbarComponent ref={this.onSetScrollbarRef}>
						{/* @ts-expect-error - TS2322 - Type '{ children: Element; width: number; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<ThemedOuterStyledProps<DiffBetween<{ style: { width: string; }; }, ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement>>, any>, any, any>> & Readonly<...> & Readonly<...>'. */}
						<HeightCorrector width={width}>
							<HeaderCells
								today={today}
								timelineWindow={timelineWindow}
								timelineMode={timelineMode}
								timelineDuration={timelineDuration}
								columnDurations={columnDurations}
								isHighlightToday={isHighlightToday}
							/>
						</HeightCorrector>
					</ScrollbarComponent>
				</Header>
			</Heading>
		);
	}
}

const secondaryBorder = `1px solid ${token('color.border', colors.N40)}`;

// RLSS-2812 replace color.background.neutral into color.background.accent.gray.subtlest
const baseHeaderStyles = css({
	width: '100%',
	height: '56px',
	boxSizing: 'border-box',
	borderBottom: secondaryBorder,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral', colors.N20),
	marginTop: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Header = styled.div(baseHeaderStyles, {
	borderRight: 'none',
	overflow: 'hidden',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HiddenScrollbar = styled.div<any>({
	height: '100%',
	overflowX: 'auto',
	overflowY: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingBottom: HIDE_SCROLLBAR_OFFSET_PX,
});

/* Set the height for this explicitly (adjusted for border widths).
 * We want to correct any calculations based on the hidden scroll parent,
 * whose height is affected by the padding and scrollbar visibility.
 * We cannot fix this through border-box since we need the padding to be
 * excluded from the height to hide the scrollbar.
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeightCorrector = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${HEADER_HEIGHT - 1}px`,
	// @ts-expect-error - TS2339 - Property 'width' does not exist on type 'ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & ThemeProps<any>'.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ width }) => ({ width: `${width}px` }),
});

const SynchronizedScrollbar = withSynchronizedScroll(HiddenScrollbar, {
	syncAxis: 'x',
	isCompiledComponent: true,
});

export default ChartHeader;
export type { APIProps };
